import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ChatHeader, MessageList, ChatInput, ChatInputRef } from './Components';
import { Message, MessageDraft } from './models';
import { ChatLanguage, ChatStrings } from './i18n';
import { ChatStringsContext } from './stringsContext';
import { useTheme } from '../../theme';
import { useHourLight } from '../../light';

export interface ChatScreenProps {
   messages: Message[];
   isBotTyping: boolean;
   /** A turn waiting for its answer has a draft: the wait line says it is still being written. */
   isBotWriting?: boolean;
   awaitingTurns?: string[];
   /** The answers still being written, shown where they will land until the final message replaces them. */
   drafts?: MessageDraft[];
   strings: ChatStrings;
   language: ChatLanguage;
   onLanguageChange: (language: ChatLanguage) => void;
   hotelName?: string;
   suggestedQuestions?: string[];
   canTypeMessages: boolean;
   isConnected: boolean;
   isConnecting: boolean;
   input: string;
   setInput: (value: string) => void;
   onSend: () => void;
   /** Sends words the guest said; the composer offers the microphone only with it. */
   onSendText?: (text: string) => void;
   onRetry?: (messageId: string) => void;
   onSuggestion?: (question: string) => void;
   children?: React.ReactNode;
}

const PANEL_GAP = 12;

/**
 * The header and composer are glass capsules floating over the column, so entries scroll under them; their
 * measured extents become the column's padding (`--cx-header-h`, `--cx-composer-h`) and follow wrapping text
 * and the keyboard.
 */
function usePanelHeights(screenRef: React.RefObject<HTMLDivElement | null>) {
   useLayoutEffect(() => {
      const screen = screenRef.current;
      if (!screen || typeof ResizeObserver === 'undefined') return;
      const header = screen.querySelector<HTMLElement>('.cx-header');
      const composer = screen.querySelector<HTMLElement>('.cx-composer');
      const apply = () => {
         const top = screen.getBoundingClientRect().top;
         const bottom = screen.getBoundingClientRect().bottom;
         if (header) screen.style.setProperty('--cx-header-h', `${Math.ceil(header.getBoundingClientRect().bottom - top + PANEL_GAP)}px`);
         if (composer) screen.style.setProperty('--cx-composer-h', `${Math.ceil(bottom - composer.getBoundingClientRect().top + PANEL_GAP)}px`);
      };
      apply();
      const observer = new ResizeObserver(apply);
      observer.observe(screen);
      if (header) observer.observe(header);
      if (composer) observer.observe(composer);
      return () => observer.disconnect();
   }, [screenRef]);
}

/**
 * The chat screen, presentational: the floating header capsule, the column of entries, the floating composer.
 * `ChatPage` wires it to the live session; the development preview renders it from fixtures.
 */
export const ChatScreen = forwardRef<ChatInputRef, ChatScreenProps>(
   (
      {
         messages,
         isBotTyping,
         isBotWriting,
         awaitingTurns,
         drafts,
         strings,
         language,
         onLanguageChange,
         hotelName,
         suggestedQuestions,
         canTypeMessages,
         isConnected,
         isConnecting,
         input,
         setInput,
         onSend,
         onSendText,
         onRetry,
         onSuggestion,
         children
      },
      ref
   ) => {
      const screenRef = useRef<HTMLDivElement | null>(null);
      usePanelHeights(screenRef);
      useHourLight();
      const { theme, toggleTheme } = useTheme();
      // Starts as if the cover is in view (it is, whenever there is one), so the capsule's name never flashes on load.
      const [coverVisible, setCoverVisible] = useState(true);
      const onCoverVisible = useCallback((visible: boolean) => setCoverVisible(visible), []);

      return (
         <ChatStringsContext.Provider value={strings}>
            <div className="cx-screen" lang={language} ref={screenRef}>
               <ChatHeader
                  hotelName={hotelName}
                  condensed={!coverVisible}
                  language={language}
                  onLanguageChange={onLanguageChange}
                  theme={theme}
                  onToggleTheme={toggleTheme}
                  strings={strings}
               />

               <MessageList
                  messages={messages}
                  isBotTyping={isBotTyping}
                  isBotWriting={isBotWriting}
                  strings={strings}
                  awaitingTurns={awaitingTurns}
                  drafts={drafts}
                  onRetry={onRetry}
                  canTypeMessages={canTypeMessages}
                  hotelName={hotelName}
                  suggestedQuestions={suggestedQuestions}
                  onSuggestion={onSuggestion}
                  onCoverVisible={onCoverVisible}
               />

               <ChatInput
                  ref={ref}
                  input={input}
                  setInput={setInput}
                  onSend={onSend}
                  onSendText={onSendText}
                  isConnected={isConnected}
                  isConnecting={isConnecting}
                  canTypeMessages={canTypeMessages}
                  strings={strings}
                  language={language}
               />

               {children}
            </div>
         </ChatStringsContext.Provider>
      );
   }
);

ChatScreen.displayName = 'ChatScreen';
